import React, { useState, useEffect, useCallback } from 'react';
import BackButton from '@/components/BackButton';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmployeeName } from '@/components/EmployeeName';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MagnifyingGlass, CheckCircle, Clock, XCircle, SpinnerGap, ShieldWarning, PencilSimple, FileXls } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getDeptLabel } from '@/constants/departments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function Qiwa() {
  const { t } = useTranslation();
  const { language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const direction = isRTL ? 'rtl' : 'ltr';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editEmployee, setEditEmployee] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/qiwa/overview`, { withCredentials: true });
      setData(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openEditDialog = (emp) => {
    setEditEmployee(emp);
    setEditForm({
      qiwa_status: emp.qiwa_status || 'not_registered',
      contract_start_date: emp.contract_start_date || '',
      contract_end_date: emp.contract_end_date || '',
      id_expiry_date: emp.id_expiry_date || '',
    });
  };

  const handleSave = async () => {
    if (!editEmployee) return;
    setSaving(true);
    try {
      await axios.put(`${API}/qiwa/employee/${editEmployee.id}`, editForm, { withCredentials: true });
      toast.success(language === 'ar' ? 'تم تحديث بيانات قوى بنجاح' : 'Qiwa data updated successfully');
      setEditEmployee(null);
      setLoading(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/qiwa/export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `qiwa_report_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'ar' ? 'تم تصدير الملف بنجاح' : 'File exported successfully');
    } catch {
      toast.error(language === 'ar' ? 'فشل تصدير الملف' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F0FA] flex items-center justify-center">
        <SpinnerGap size={36} className="animate-spin text-[#655dc6]" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const employees = data?.employees || [];

  const filteredEmployees = employees.filter(emp => {
    const matchSearch = !search || emp.name.includes(search) || (emp.name_en || '').toLowerCase().includes(search.toLowerCase()) || (emp.iqama_number || '').includes(search) || (emp.employee_number || '').includes(search);
    let matchFilter = true;
    if (filter === 'active') matchFilter = emp.qiwa_status === 'active';
    else if (filter === 'not_registered') matchFilter = !['active', 'processing'].includes(emp.qiwa_status);
    else if (filter === 'processing') matchFilter = emp.qiwa_status === 'processing';
    else if (filter === 'alerts') matchFilter = emp.alerts.length > 0;
    return matchSearch && matchFilter;
  }).sort((a, b) => {
    // Default ascending order by numeric employee_number — matches the
    // sorting users see on the Employees roster.
    const an = parseInt(a.employee_number, 10);
    const bn = parseInt(b.employee_number, 10);
    if (isNaN(an) && isNaN(bn)) return 0;
    if (isNaN(an)) return 1;
    if (isNaN(bn)) return -1;
    return an - bn;
  });

  const getDaysRemaining = (dateStr) => {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const qiwaStatusBadge = (status) => {
    if (status === 'active') return <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 whitespace-nowrap"><CheckCircle size={11} weight="fill" />{t('active_on_qiwa_count')}</span>;
    if (status === 'processing') return <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap"><Clock size={11} weight="fill" />{t('processing_count')}</span>;
    return <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 whitespace-nowrap"><XCircle size={11} weight="fill" />{t('not_registered_count')}</span>;
  };

  const severityBadge = (alert) => {
    const s = alert.severity;
    if (s === 'critical') return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">{t(alert.type)}</span>;
    if (s === 'high') return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-bold">{t(alert.type)}</span>;
    if (s === 'medium') return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{t(alert.type)}</span>;
    return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{t(alert.type)}</span>;
  };

  const dateBadge = (dateStr) => {
    if (!dateStr) return <span className="text-gray-400 text-xs">-</span>;
    const days = getDaysRemaining(dateStr);
    let color = 'text-green-600';
    if (days < 0) color = 'text-red-600 font-bold';
    else if (days <= 30) color = 'text-red-500';
    else if (days <= 90) color = 'text-amber-500';
    return (
      <div className="text-[10px] text-center whitespace-nowrap">
        <span className="text-[#655dc6]/60">{dateStr}</span>
        <br />
        <span className={color}>
          {days < 0 ? `${t('expired_since')} ${Math.abs(days)} ${t('days')}` : `${days} ${t('days')}`}
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction} data-testid="qiwa-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#655dc6]">{t('qiwa_management')}</h1>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-[#059669] hover:bg-[#047857] text-white gap-2"
            data-testid="export-qiwa-btn"
          >
            {exporting ? <SpinnerGap size={18} className="animate-spin" /> : <FileXls size={18} weight="duotone" />}
            {language === 'ar' ? 'تصدير Excel' : 'Export Excel'}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="qiwa-stats">
          <StatCard label={t('all_employees')} value={stats.total} color="bg-[#655dc6]" testId="stat-total" />
          <StatCard label={t('active_on_qiwa_count')} value={stats.active_qiwa} color="bg-green-600" testId="stat-active" />
          <StatCard label={t('not_registered_count')} value={stats.not_registered} color="bg-red-500" testId="stat-not-reg" />
          <StatCard label={t('processing_count')} value={stats.processing} color="bg-amber-500" testId="stat-processing" />
        </div>

        {/* Alerts Summary */}
        {(stats.critical_alerts > 0 || stats.high_alerts > 0) && (
          <Card className="border-red-200 bg-red-50/50" data-testid="qiwa-alerts-banner">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-3 flex-wrap">
                <ShieldWarning size={22} className="text-red-600" weight="fill" />
                <span className="font-semibold text-red-700 text-sm">{t('qiwa_alerts')}:</span>
                {stats.contract_expired > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{t('contract_expired')}: {stats.contract_expired}</span>}
                {stats.id_expired > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{t('id_expired')}: {stats.id_expired}</span>}
                {stats.contract_expiring_soon > 0 && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{t('contract_expiring_soon')}: {stats.contract_expiring_soon}</span>}
                {stats.id_expiring_soon > 0 && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{t('id_expiring_soon')}: {stats.id_expiring_soon}</span>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter & Search */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlass size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-[#655dc6]/40" />
            <Input
              className="ps-9 border-[#655dc6]/15"
              placeholder={language === 'ar' ? 'بحث بالاسم أو رقم الهوية...' : 'Search by name or ID...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="qiwa-search"
            />
          </div>
          {['all', 'active', 'not_registered', 'processing', 'alerts'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              className={filter === f ? 'bg-[#655dc6] hover:bg-[#544cb0]' : 'border-[#655dc6]/20 text-[#655dc6]'}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? t('all_employees') : f === 'alerts' ? t('alerts') : t(`${f === 'active' ? 'active_on_qiwa_count' : f === 'not_registered' ? 'not_registered_count' : 'processing_count'}`)}
              <span className="ms-1 text-xs opacity-70">
                ({f === 'all' ? employees.length : f === 'alerts' ? employees.filter(e => e.alerts.length > 0).length : employees.filter(e => f === 'active' ? e.qiwa_status === 'active' : f === 'not_registered' ? !['active', 'processing'].includes(e.qiwa_status) : e.qiwa_status === 'processing').length})
              </span>
            </Button>
          ))}
        </div>

        {/* Employees Table */}
        <Card className="border-[#655dc6]/10">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="qiwa-table">
                <TableHeader>
                  <TableRow className="border-[#C4BFF0]">
                    <TableHead className="text-start text-[#655dc6] text-xs whitespace-nowrap">{t('employee_name')}</TableHead>
                    <TableHead className="!text-center text-[#655dc6] text-xs whitespace-nowrap">{t('employee_number')}</TableHead>
                    <TableHead className="!text-center text-[#655dc6] text-xs whitespace-nowrap">{t('department')}</TableHead>
                    <TableHead className="!text-center text-[#655dc6] text-xs whitespace-nowrap">{t('iqama_number')}</TableHead>
                    <TableHead className="!text-center text-[#655dc6] text-xs whitespace-nowrap">{t('contract_end_date')}</TableHead>
                    <TableHead className="!text-center text-[#655dc6] text-xs whitespace-nowrap">{t('id_expiry_date')}</TableHead>
                    <TableHead className="!text-center text-[#655dc6] text-xs whitespace-nowrap">{t('qiwa_status')}</TableHead>
                    <TableHead className="!text-center text-[#655dc6] text-xs whitespace-nowrap">{t('alerts')}</TableHead>
                    <TableHead className="!text-center text-[#655dc6] text-xs whitespace-nowrap">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-[#655dc6]/40">{t('no_data')}</TableCell>
                    </TableRow>
                  ) : (
                    filteredEmployees.map(emp => (
                      <TableRow key={emp.id} className={emp.alerts.some(a => a.severity === 'critical') ? 'bg-red-50/40' : ''} data-testid={`qiwa-row-${emp.id}`}>
                        <TableCell>
                          <EmployeeName name={emp.name} nameEn={emp.name_en} badgeData={emp} badgeLanguage={language} />
                        </TableCell>
                        <TableCell className="text-sm text-[#655dc6]/70 font-mono text-center" dir="ltr">{emp.employee_number || '-'}</TableCell>
                        <TableCell className="text-sm text-[#655dc6]/70 text-center">{getDeptLabel(emp.department, language)}</TableCell>
                        <TableCell className="text-sm text-[#655dc6]/70 font-mono text-center" dir="ltr">{emp.iqama_number || '-'}</TableCell>
                        <TableCell className="text-center">{dateBadge(emp.contract_end_date)}</TableCell>
                        <TableCell className="text-center">{dateBadge(emp.id_expiry_date)}</TableCell>
                        <TableCell className="text-center">{qiwaStatusBadge(emp.qiwa_status)}</TableCell>
                        <TableCell className="text-center">
                          {emp.alerts.length === 0 ? (
                            <CheckCircle size={16} className="text-green-500 mx-auto" weight="fill" />
                          ) : (
                            <div className="flex flex-wrap gap-1 justify-center">
                              {emp.alerts.map((a, i) => (
                                <span key={i}>{severityBadge(a)}</span>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-[#655dc6] hover:bg-[#E8E6F5] mx-auto"
                            onClick={() => openEditDialog(emp)}
                            data-testid={`edit-qiwa-${emp.id}`}
                          >
                            <PencilSimple size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Edit Qiwa Dialog */}
      <Dialog open={!!editEmployee} onOpenChange={(open) => { if (!open) setEditEmployee(null); }}>
        <DialogContent className="sm:max-w-[480px]" dir={direction} data-testid="qiwa-edit-dialog">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#655dc6]">
                <PencilSimple size={22} weight="duotone" />
                {language === 'ar' ? 'تعديل بيانات قوى' : 'Edit Qiwa Data'}
              </DialogTitle>
              <DialogDescription>
                {editEmployee?.name} {editEmployee?.name_en ? `(${editEmployee.name_en})` : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('qiwa_status')}</Label>
                <Select value={editForm.qiwa_status} onValueChange={v => setEditForm(p => ({ ...p, qiwa_status: v }))}>
                  <SelectTrigger data-testid="edit-qiwa-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('active_on_qiwa_count')}</SelectItem>
                    <SelectItem value="not_registered">{t('not_registered_count')}</SelectItem>
                    <SelectItem value="processing">{t('processing_count')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('contract_start_date')}</Label>
                  <Input
                    type="date"
                    value={editForm.contract_start_date}
                    onChange={e => setEditForm(p => ({ ...p, contract_start_date: e.target.value }))}
                    dir="ltr"
                    data-testid="edit-contract-start"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('contract_end_date')}</Label>
                  <Input
                    type="date"
                    value={editForm.contract_end_date}
                    onChange={e => setEditForm(p => ({ ...p, contract_end_date: e.target.value }))}
                    dir="ltr"
                    data-testid="edit-contract-end"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('id_expiry_date')}</Label>
                <Input
                  type="date"
                  value={editForm.id_expiry_date}
                  onChange={e => setEditForm(p => ({ ...p, id_expiry_date: e.target.value }))}
                  dir="ltr"
                  data-testid="edit-id-expiry"
                />
              </div>
            </div>
            <DialogFooter className="mt-5">
              <Button onClick={handleSave} disabled={saving} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="save-qiwa-btn">
                {saving ? <SpinnerGap size={20} className="animate-spin" /> : t('save')}
              </Button>
              <Button variant="outline" onClick={() => setEditEmployee(null)}>{t('cancel')}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, color, testId }) {
  return (
    <div className={`${color} rounded-xl p-4 text-white`} data-testid={testId}>
      <p className="text-xs opacity-80">{label}</p>
      <p className="text-3xl font-bold mt-1">{value || 0}</p>
    </div>
  );
}
